import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import { isFeatureAllowed } from "./drmPolicyService";
import {
  createHighlight as createHighlightApi,
  deleteHighlight as deleteHighlightApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
  updateHighlight as updateHighlightApi,
} from "./igpService";
import {
  showFeatureNotAllowedNotification,
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * Highlight service using IGP API as source-of-truth.
 * LocalStorage usage removed — highlights are stored in the global `useReaderStore`.
 */

export async function refreshHighlightsFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, ["highlights"]);
    // API returns highlights directly under `highlights.data` in the returned `data` object
    const highlights = accessData?.highlights?.data || [];
    // Update the global store
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("highlights", highlights);
    return highlights;
  } catch (err) {
    console.error("Error refreshing highlights from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

/**
 * Internal: Persist highlight to API in background with error handling.
 * Removes from store on error.
 */
async function _persistHighlightToApi(
  operation,
  highlightId,
  highlightData,
  isEdit = false,
  oldHighlight = null,
) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    if (operation === "create") {
      await createHighlightApi(grantId, rstToken, highlightData);
    } else if (operation === "update") {
      await updateHighlightApi(grantId, rstToken, highlightId, highlightData);
    }
  } catch (err) {
    console.error(`Error persisting highlight (${operation}):`, err);

    // Rollback logic
    const state = useReaderStore.getState();
    const currentHighlights = state.annotations.highlights.data || [];

    if (operation === "create") {
      state.setAnnotationData(
        "highlights",
        currentHighlights.filter((h) => h.id !== highlightId),
      );
    } else if (operation === "update" && oldHighlight) {
      state.setAnnotationData(
        "highlights",
        currentHighlights.map((h) => (h.id === highlightId ? oldHighlight : h)),
      );
    }

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(
        operation === "create" ? i18n.t("notifications.operations.saveHighlight") : i18n.t("notifications.operations.updateHighlight"),
      );
    }
  }
}

/**
 * Create highlight with OPTIMISTIC UPDATE.
 * Returns immediately with optimistic highlight.
 * API persists in background.
 */
export function createHighlight(highlightData) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData, drmPolicy } = state;

  // Enforce DRM policy
  if (!isFeatureAllowed(drmPolicy, "highlights")) {
    showFeatureNotAllowedNotification("highlights");
    return null;
  }

  const tempId =
    highlightData.id || `hl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const optimisticHighlight = {
    ...highlightData,
    id: tempId,
    timestamp: highlightData.timestamp || new Date().toISOString(),
    _isPending: true,
  };

  // Add to store immediately
  const current = annotations.highlights.data || [];
  setAnnotationData("highlights", [...current, optimisticHighlight]);

  // Fire API in background (include tempId so the API uses the same ID as the store)
  _persistHighlightToApi("create", tempId, { ...highlightData, id: tempId });

  return optimisticHighlight;
}

/**
 * Edit highlight with OPTIMISTIC UPDATE.
 */
export function editHighlight(highlightId, updateData) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData, drmPolicy } = state;

  if (!isFeatureAllowed(drmPolicy, "highlights")) {
    showFeatureNotAllowedNotification("highlights");
    return null;
  }

  const apiId = toApiHighlightId(highlightId);
  const current = annotations.highlights.data || [];
  const oldHighlight = current.find((h) => h.id === apiId);

  if (!oldHighlight) return null;

  // No-op if nothing actually changed (avoids redundant API traffic)
  const hasMeaningfulChange = Object.keys(updateData || {}).some(
    (key) => updateData[key] !== oldHighlight[key],
  );
  if (!hasMeaningfulChange) {
    return oldHighlight;
  }

  const updatedHighlight = {
    ...oldHighlight,
    ...updateData,
    _isPending: true,
  };

  // Update store immediately
  setAnnotationData(
    "highlights",
    current.map((h) => (h.id === apiId ? updatedHighlight : h)),
  );

  // Fire API in background
  _persistHighlightToApi("update", apiId, updateData, true, oldHighlight);

  return updatedHighlight;
}

/**
 * Resolve to the API (parent) highlight id. API expects one id per highlight;
 * viewers may use rect ids like "id-rect-0" for multi-rect highlights.
 */
function toApiHighlightId(highlightId) {
  if (!highlightId || typeof highlightId !== "string") return highlightId;
  return highlightId.replace(/-rect-\d+$/, "") || highlightId;
}

/**
 * Delete highlight with OPTIMISTIC UPDATE.
 */
export function removeHighlightById(highlightId) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData } = state;

  const apiId = toApiHighlightId(highlightId);
  const current = annotations.highlights.data || [];
  const highlightToDelete = current.find((h) => h.id === apiId);

  if (!highlightToDelete) return null;

  // Remove from store immediately
  setAnnotationData(
    "highlights",
    current.filter((h) => h.id !== apiId),
  );

  // Fire API in background
  _persistHighlightDelete(apiId, highlightToDelete);

  return highlightToDelete;
}

/**
 * Internal: Special handling for delete with rollback.
 */
async function _persistHighlightDelete(highlightId, highlightData) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteHighlightApi(grantId, rstToken, highlightId);
  } catch (err) {
    console.error("Error deleting highlight:", err);

    // Rollback: Re-add to store
    const state = useReaderStore.getState();
    const current = state.annotations.highlights.data || [];
    state.setAnnotationData("highlights", [...current, highlightData]);

    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteHighlight"));
    }
  }
}


export default useReaderStore;
